import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader, useToast } from 'components/admin';
import { Button, Checkbox, Input, Select } from 'components/common';
import { createNavMenu, getAdminNavMenuById, getAllAdminNavMenus, updateNavMenu } from 'apis/adminNavMenus';
import { useActiveLanguagesQuery, useAdminCreateMutation, useAdminDetailQuery, useAdminListQuery, useAdminUpdateMutation } from 'hooks';
import '../../../styles/admin-shared.scss';

const NavMenusForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState(0);
    const [formData, setFormData] = useState({
        keyName: '',
        parentId: '',
        isActive: true,
        sortOrder: 0,
        level: 1,
        translation: []
    });

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: navMenusData = { items: [] } } = useAdminListQuery('nav-menus-options', { pageSize: 1000 }, getAllAdminNavMenus);
    const { data: detail, error: detailError } = useAdminDetailQuery('nav-menus', id, getAdminNavMenuById);
    const createMutation = useAdminCreateMutation('nav-menus', createNavMenu);
    const updateMutation = useAdminUpdateMutation('nav-menus', updateNavMenu);
    const loading = createMutation.isPending || updateMutation.isPending;

    const buildTree = (items) => {
        const map = {};
        const roots = [];
        items.forEach(item => { map[item.id] = { ...item, children: [] }; });
        items.forEach(item => {
            if (item.parentId && map[item.parentId]) map[item.parentId].children.push(map[item.id]);
            else roots.push(map[item.id]);
        });
        return roots;
    };

    const flatten = (nodes, level = 0) => nodes.flatMap(node => [
        { ...node, _level: level },
        ...(node.children?.length ? flatten(node.children, level + 1) : []),
    ]);

    const mappedMenus = navMenusData.items.map(m => ({ ...m, id: m.Id || m.id, parentId: m.ParentId || m.parentId }));
    const navMenus = flatten(buildTree(mappedMenus)).filter(m => !id || m.id !== Number(id));

    useEffect(() => {
        if (!id && languages.length) {
            setFormData(prev => ({ ...prev, translation: languages.map(l => ({ languageId: l.id, title: '', slug: '' })) }));
        }
    }, [id, languages]);

    useEffect(() => {
        if (detail) {
            setFormData({
                keyName: detail.keyName || '',
                parentId: detail.parentId || '',
                isActive: detail.isActive ?? true,
                sortOrder: detail.sortOrder || 0,
                level: detail.level ?? detail.Level ?? 1,
                translation: detail.translation || detail.translations || [],
            });
        }
    }, [detail]);

    useEffect(() => { if (detailError) toast.error(detailError.message || 'T?i d? li?u th?t b?i'); }, [detailError, toast]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleTranslationChange = (langId, field, value) => {
        setFormData(prev => ({
            ...prev,
            translation: prev.translation.map(t => t.languageId === langId ? { ...t, [field]: value } : t),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                parentId: formData.parentId ? Number(formData.parentId) : null,
                sortOrder: Number(formData.sortOrder),
                level: Number(formData.level) || 1
            };
            const res = id ? await updateMutation.mutateAsync({ id, data: payload }) : await createMutation.mutateAsync(payload);
            if (res.success) {
                toast.success(id ? 'Cập nhật thành công' : 'Thêm mới thành công');
                navigate('/admin/nav-menus');
            } else {
                if (res.messageError) setErrors(res.messageError);
                else toast.error(res.message);
            }
        } catch {
            toast.error('Lưu thất bại');
        }
    };

    return (
        <div className="admin-page">
            <PageHeader title={id ? 'Cập nhật Menu Điều Hướng' : 'Thêm Menu Điều Hướng'} subtitle="Cấu hình mục menu điều hướng" />
            <form className="form-card" onSubmit={handleSubmit}>
                <p className="form-section-title">Thông tin Menu</p>
                <div className="form-row">
                    <Input label="Tên Key *" name="keyName" value={formData.keyName} onChange={handleChange} required error={errors.keyName} placeholder="vd: about-us" disabled={!!id} />
                    <Select
                        label="Menu Cha"
                        name="parentId"
                        value={formData.parentId}
                        onChange={handleChange}
                        options={[{ value: '', label: 'Không có (Cấp cao nhất)' }, ...navMenus.map(m => {
                            const prefix = m._level > 0 ? '└─'.padStart(m._level * 2, '─') + ' ' : '';
                            const title = m.title || m.Title || m.name || m.Name || m.keyName || m.KeyName || m.translations?.[0]?.title || m.translations?.[0]?.name || "Không tên";
                            return {
                                value: m.id,
                                label: `${prefix}${title}`
                            };
                        })]}
                        error={errors.parentId}
                    />
                </div>
                <div className="form-row">
                    <Input label="Thứ tự sắp xếp" name="sortOrder" type="number" value={formData.sortOrder} onChange={handleChange} />
                    <Input
                        label="Cấp hiển thị (Level)"
                        name="level"
                        type="number"
                        min={1}
                        value={formData.level}
                        onChange={handleChange}
                        placeholder="vd: 1 (Menu chính), 2 (Menu Hotline), ..."
                    />
                </div>
                <Checkbox label="Hoạt động" name="isActive" checked={formData.isActive} onChange={handleChange} />

                {languages.length > 0 && (
                    <>
                        <p className="form-section-title">Bản dịch</p>
                        <div className="translation-tabs">
                            <div className="tab-header">
                                {languages.map((lang, i) => (
                                    <button key={lang.id} type="button" className={`tab-btn ${activeTab === i ? 'active' : ''}`} onClick={() => setActiveTab(i)}>
                                        {lang.name} ({lang.code})
                                    </button>
                                ))}
                            </div>
                            {languages.map((lang, i) => {
                                const t = formData.translation.find(t => t.languageId === lang.id) || {};
                                return activeTab === i ? (
                                    <div key={lang.id} className="tab-content">
                                        <Input label="Tiêu đề *" value={t.title || ''} onChange={e => handleTranslationChange(lang.id, 'title', e.target.value)} required />
                                        <Input label="Slug" value={t.slug || ''} onChange={e => handleTranslationChange(lang.id, 'slug', e.target.value)} />
                                    </div>
                                ) : null;
                            })}
                        </div>
                    </>
                )}

                <div className="form-actions">
                    <Button type="submit" disabled={loading}>{loading ? 'Đang lưu...' : id ? 'Cập nhật Menu' : 'Thêm Menu'}</Button>
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/nav-menus')}>Hủy</Button>
                </div>
            </form>
        </div>
    );
};

export default NavMenusForm;
